<template>
    <div class="salesforce-activity-settings mt-4">
        <h5 class="text-black">Activity Settings</h5>
        <table class="table subject-settings-table">
            <thead>
            <tr>
                <td colspan="2" class="text-center">
                    <div class="section-header">
                        <strong>Subject Format</strong>
                        <el-tooltip class="item" effect="dark" placement="right">
                            <div slot="content">
                                When a task is created, the subject line will be dynamically generated using the selected fields.
                            </div>
                            <i class="material-icons text-lg cursor-pointer text-blue p-0">info_outline</i>
                        </el-tooltip>
                    </div>
                </td>
            </tr>
            <tr>
                <th class="text-center">Call</th>
                <th class="text-center">SMS</th>
            </tr>
            </thead>
            <tbody>
            <tr>
                <td>
                    <el-checkbox-group class="vertical-checkboxes" v-model="model.settings.activities_settings.call">
                        <el-checkbox v-for="field in callSubjectFields" :key="field.value" :label="field.value">
                            {{ field.label }}
                            <el-tooltip class="item" effect="dark" placement="top">
                                <div slot="content">{{ field.description || '' }}</div>
                                <i class="material-icons text-lg cursor-pointer text-blue p-0">info_outline</i>
                            </el-tooltip>
                        </el-checkbox>
                    </el-checkbox-group>
                </td>
                <td>
                    <el-checkbox-group class="vertical-checkboxes" v-model="model.settings.activities_settings.sms">
                        <el-checkbox v-for="field in smsSubjectFields" :key="field.value" :label="field.value">
                            {{ field.label }}
                            <el-tooltip class="item" effect="dark" placement="top">
                                <div slot="content">{{ field.description || '' }}</div>
                                <i class="material-icons text-lg cursor-pointer text-blue p-0">info_outline</i>
                            </el-tooltip>
                        </el-checkbox>
                    </el-checkbox-group>
                </td>
            </tr>
            </tbody>
        </table>
        <table class="table description-settings-table">
            <thead>
            <tr>
                <td colspan="2" class="text-center">
                    <div class="section-header">
                        <strong>Description Settings</strong>
                        <el-tooltip class="item" effect="dark" placement="right">
                            <div slot="content">
                                When a task is created, the description will be dynamically generated using the selected fields.
                            </div>
                            <i class="material-icons text-lg cursor-pointer text-blue p-0">info_outline</i>
                        </el-tooltip>
                    </div>
                </td>
            </tr>
            <tr>
                <th class="text-center">Call</th>
                <th class="text-center">SMS</th>
            </tr>
            </thead>
            <tbody>
            <tr>
                <td>
                    <el-checkbox-group class="vertical-checkboxes" v-model="model.settings.custom_activity_description_sections.call">
                        <el-checkbox v-for="field in callDescriptionFields" :key="field.value" :label="field.value">
                            {{ field.label }}
                            <el-tooltip class="item" effect="dark" placement="top">
                                <div slot="content">{{ field.description || '' }}</div>
                                <i class="material-icons text-lg cursor-pointer text-blue p-0">info_outline</i>
                            </el-tooltip>
                        </el-checkbox>
                    </el-checkbox-group>
                </td>
                <td>
                    <el-checkbox-group class="vertical-checkboxes" v-model="model.settings.custom_activity_description_sections.sms">
                        <el-checkbox v-for="field in smsDescriptionFields" :key="field.value" :label="field.value">
                            {{ field.label }}
                            <el-tooltip class="item" effect="dark" placement="top">
                                <div slot="content">{{ field.description || '' }}</div>
                                <i class="material-icons text-lg cursor-pointer text-blue p-0">info_outline</i>
                            </el-tooltip>
                        </el-checkbox>
                    </el-checkbox-group>
                </td>
            </tr>
            </tbody>
        </table>
    </div>
</template>
<script>
export default {
    props: {
        model: {
            type: Object,
            required: true,
        },
    },
    created() {
      if (!this.model.settings.activities_settings) {
        this.$set(this.model.settings, 'activities_settings', {})
      }

      if (!this.model.settings.activities_settings.call || this.model.settings.activities_settings.call.length === 0) {
        // Default for Call
        this.$set(this.model.settings.activities_settings, 'call', ['call_disposition', 'to'])
      }

      if (!this.model.settings.activities_settings.sms || this.model.settings.activities_settings.sms.length === 0) {
        // Default for SMS
        this.$set(this.model.settings.activities_settings, 'sms', ['from', 'communication_direction', 'to'])
      }

      if (!this.model.settings.custom_activity_description_sections) {
        this.$set(this.model.settings, 'custom_activity_description_sections', {})
      }

      if (!this.model.settings.custom_activity_description_sections.call || this.model.settings.custom_activity_description_sections.call.length === 0) {
        // Default for Call
        this.$set(this.model.settings.custom_activity_description_sections, 'call', this.callDescriptionFields.map(field => field.value))
      }

      if (!this.model.settings.custom_activity_description_sections.sms || this.model.settings.custom_activity_description_sections.sms.length === 0) {
        // Default for SMS
        this.$set(this.model.settings.custom_activity_description_sections, 'sms', this.smsDescriptionFields.map(field => field.value))
      }
  },
  computed: {
        callDescriptionFields() {
            return [
                { value: "call_completed", label: "Call Completed", description: "Date/Time that the call was completed" },
                { value: "call_outcome", label: "Call Outcome", description: "Result/Outcome of the call" },
                { value: "call_duration", label: "Call Duration", description: "Length of the call" },
                { value: "call_direction", label: "Call Direction", description: "Direction of the call (Inbound/Outbound)" },
                { value: "line", label: "Line", description: "Phone line used" },
                { value: "from", label: "From", description: "Caller information" },
                { value: "to", label: "To", description: "Call recipient" },
                { value: "recording_url", label: "Recording URL", description: "Link to the call recording" },
                { value: "voicemail_url", label: "Voicemail URL", description: "Link to the voicemail" },
                { value: "tags", label: "Tags", description: "Tags associated with the call" },
                { value: "notes", label: "Notes", description: "Additional notes" },
                { value: "source", label: "Source", description: "The source of the call" },
            ];
        },
        smsDescriptionFields() {
            return [
                { value: "message_date", label: "Message Date", description: "Date/Time the message was sent" },
                { value: "message", label: "Message", description: "Content of the message" },
                { value: "direction", label: "Direction", description: "Message direction (Inbound/Outbound)" },
                { value: "line", label: "Line", description: "Phone line used" },
                { value: "from", label: "From", description: "Sender information" },
                { value: "to", label: "To", description: "Message recipient" },
                { value: "attachments", label: "Attachments", description: "Attached files" },
                { value: "tags", label: "Tags", description: "Tags associated with the message" },
                { value: "notes", label: "Notes", description: "Additional notes" },
                { value: "source", label: "Source", description: "The source of the message" },
            ];
        },
        callSubjectFields() {
            return [
                { value: "call_disposition", label: "Call Outcome (Disposition)", description: "Result/Outcome of the call" },
                { value: "from", label: "From", description: "Indicates the assigned owner of a task. Who called (in call)" },
                { value: "communication_direction", label: "Communication Direction", description: "Communication direction Inbound or Outbound" },
                { value: "to", label: "To", description: "Who received the call or message" },
            ];
        },
        smsSubjectFields() {
            return [
                { value: "from", label: "From", description: "Indicates the assigned owner of a task. Who Sent the message (in SMS)" },
                { value: "communication_direction", label: "Communication Direction", description: "Communication direction Inbound or Outbound" },
                { value: "to", label: "To", description: "Who received the call or message" },
            ];
        }
    }
}
</script>
<style scoped>
.vertical-checkboxes {
    display: flex;
    flex-direction: column;
    gap: 8px;
}
</style>
